import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { LoggingService } from '@app/core/services';

import { environment } from '../../../environments/environment';

@Injectable({
    providedIn: 'root',
})
export class DownloadService {
    private readonly sessionsApiRoot: string = `${environment.apiRoot}sessions`;
    private readonly completedFilesApiRoot: string = `${environment.apiRoot}files/completed`;

    constructor(
        private http: HttpClient,
        private loggingService: LoggingService
        ) {}

    public downloadFile(guid: string): void {
        if (!guid) {
            this.loggingService.error('GUID is required to download the file.');
            return;
        }

        this.loggingService.warn('downloadFile is deprecated and no longer mapped to a current API route.');
    }

    public downloadFolder(folderName: string): void {
        if (!folderName) {
            this.loggingService.error('Folder name is required to download the folder.');
            return;
        }

        const downloadUrl = `${this.completedFilesApiRoot}/${encodeURIComponent(folderName)}/download`;

        // Use HttpClient to fetch the folder as a blob and assign a zip filename
        this.http.get(downloadUrl, { observe: 'response', responseType: 'blob' }).subscribe({
            next: (response) => {
                const fileName = `${folderName}.zip`; // Default filename for folders
                this.loggingService.info('Download URL:', downloadUrl);
                this.loggingService.info('Folder name:', fileName);

                // Trigger download
                this.triggerDownloadBlob(response.body, fileName);
            },
            error: (err) => {
                this.loggingService.error('Error downloading the folder:', err);
            },
        });
    }

    public downloadAllFiles(): void {
        this.loggingService.warn('downloadAllFiles is deprecated and no longer mapped to a current API route.');
    }

    private triggerDownloadBlob(blob: Blob | null, fileName: string): void {
        if (!blob) {
            this.loggingService.error('Blob data is required to trigger a download.');
            return;
        }

        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = fileName;
        link.click();
        window.URL.revokeObjectURL(link.href);
        this.loggingService.info(`${fileName} download triggered.`);
    }

    public downloadFileByPath(resultsFilePath: string): void {
        const downloadUrl = this.buildSessionFileDownloadUrl(resultsFilePath);
        if (!downloadUrl) {
            this.loggingService.error('Unable to resolve a session file download URL from the provided path.');
            return;
        }
        this.loggingService.info('Download URL:', downloadUrl);

        this.http.get(downloadUrl, { responseType: 'blob' }).subscribe({
            next: (response) => {
                const fileName = this.extractFileName(resultsFilePath);
                const url = window.URL.createObjectURL(response);
                const a = document.createElement('a');
                a.href = url;
                a.download = fileName;
                a.click();
                window.URL.revokeObjectURL(url);
            },
            error: (err) => {
                this.loggingService.error('Error during file download:', err);
            },
        });
    }

    /**
     * Downloads single or multiple files.
     * If multiple files are provided, they are returned as a ZIP file.
     * @param filePaths - Array of file paths to be downloaded.
     */
    public downloadFilesByPaths(filePaths: string[]): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            if (!filePaths || filePaths.length === 0) {
                this.loggingService.error('File paths are required for downloading files.');
                reject(new Error('File paths are required for downloading files.'));
                return;
            }

            const parsed = this.parseSessionPath(filePaths[0]);
            if (!parsed) {
                this.loggingService.error('Unable to resolve a session download URL from the provided file paths.');
                reject(new Error('Unable to resolve a session download URL from the provided file paths.'));
                return;
            }

            const sameSession = filePaths.every((path) => {
                const current = this.parseSessionPath(path);
                return current?.sessionId === parsed.sessionId;
            });

            if (!sameSession) {
                this.loggingService.error('All file paths must belong to the same session.');
                reject(new Error('All file paths must belong to the same session.'));
                return;
            }

            const downloadUrl = `${this.sessionsApiRoot}/${encodeURIComponent(parsed.sessionId)}/files/download`;
            let params = new HttpParams();
            filePaths.forEach((path) => {
                const current = this.parseSessionPath(path);
                if (current?.fileName) {
                    params = params.append('fileNames', current.fileName);
                }
            });

            this.http
                .get(downloadUrl, {
                    responseType: 'blob',
                    observe: 'response',
                    params,
                })
                .subscribe({
                    next: (response) => {
                        const contentDisposition = response.headers.get('Content-Disposition');
                        const fileName =
                            this.extractFileNameFromContentDisposition(contentDisposition) ||
                            this.getDefaultFileName(filePaths);

                        // Trigger the file download
                        this.triggerDownloadBlob(response.body, fileName);
                        resolve(); // Resolve the promise on success
                    },
                    error: (err) => {
                        this.loggingService.error('Error downloading files:', err);
                        reject(err); // Reject the promise on error
                    },
                });
        });
    }

    /**
     * Extracts the file name from the Content-Disposition header.
     * @param contentDisposition - The Content-Disposition header value.
     */
    private extractFileNameFromContentDisposition(
        contentDisposition: string | null
    ): string | null {
        if (!contentDisposition) return null;

        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        return match ? match[1] : null;
    }

    /**
     * Returns a default file name based on input file paths.
     * @param filePaths - The list of file paths provided for download.
     */
    private getDefaultFileName(filePaths: string[]): string {
        return filePaths.length === 1
            ? this.extractFileName(filePaths[0]) // Single file name
            : 'Files.zip'; // Default for multiple files
    }

    /**
     * Extracts the file name from a file path.
     * @param filePath - Full file path.
     */
    private extractFileName(filePath: string): string {
        return filePath.substring(filePath.lastIndexOf('/') + 1);
    }

    private buildSessionFileDownloadUrl(resultsFilePath: string): string {
        const parsed = this.parseSessionPath(resultsFilePath);
        if (!parsed) {
            return '';
        }

        return `${this.sessionsApiRoot}/${encodeURIComponent(parsed.sessionId)}/files/${encodeURIComponent(parsed.fileName)}/download`;
    }

    private parseSessionPath(filePath: string): { sessionId: string; fileName: string } | null {
        const normalized = (filePath || '').replace(/\\/g, '/').replace(/\/+$/, '');
        const parts = normalized.split('/').filter((segment) => !!segment);
        if (parts.length < 2) {
            return null;
        }

        return {
            sessionId: parts[parts.length - 2],
            fileName: parts[parts.length - 1],
        };
    }
}
